import { CandidateOpportunitiesConst } from './candidate-opportunities';
import { CountryCodesConst } from './country-codes';

const { CountryCodesAbbreviations } = CountryCodesConst;

/** Candidate */
export const DefaultWeeklyHours = {
  maxHours: 40,
  minHours: 30,
  dailyHours: 8,
} as const;
Object.freeze(DefaultWeeklyHours);

const candidateFreeFilterColumns = [
  'compensation',
  'practiceStartYear',
  'weeklyAvailability',
] as const;

const TALENT_VIEW_SORT_COLUMNS = [
  'relevance',
  'addressCity',
  'calculatedLastName',
  'ownerUser.lastName',
  'profileStatus',
  'soonestEngagementEndDate',
  'updatedAt',
  'weeklyAvailability',
  'compensation',
] as const;

const BENCH_VIEW_SORT_COLUMNS = [
  ...TALENT_VIEW_SORT_COLUMNS,
  'alphabeticallySmallestEngagementAccountName',
] as const;

export const Candidate = {
  FILTER_COLUMNS: [
    ...candidateFreeFilterColumns,
    'address1',
    'address2',
    'addressCity',
    'addressCountry',
    'addressCountryCode',
    'addressState',
    'addressZip',
    'integrationId',
    'calculatedDisplayName',
    'calculatedFirstName',
    'calculatedLastName',
    'displayFirstName',
    'displayLastName',
    'employeeType',
    'firstName',
    'furthestCandidacyStatus',
    'homePhone',
    'lastName',
    'lawSchool',
    'middleName',
    'mobilePhone',
    'personalEmail',
    'profileStatus',
    'resume',
    'workEmail',
  ],
  FREE_FILTER_COLUMNS: candidateFreeFilterColumns,
  SORT_COLUMNS: [
    ...new Set([...TALENT_VIEW_SORT_COLUMNS, ...BENCH_VIEW_SORT_COLUMNS]),
  ],
  SORT_COLUMNS_NAMES_MAP: {
    calculatedLastName: 'Talent Name',
    profileStatus: 'Employment Status',
    addressCity: 'Residence',
    ownerLastName: 'Talent Owner',
    updatedAt: 'Last Activity Date',
    soonestEngagementEndDate: 'Roll Off Date',
    alphabeticallySmallestEngagementAccountName: 'Account Name',
  },
  TALENT_VIEW_SORT_COLUMNS,
  BENCH_VIEW_SORT_COLUMNS,

  /** field names */
  ACTIVITY_LOG: 'activityLog',
  ADDRESS_1: 'address1',
  ADDRESS_2: 'address2',
  ADDRESS_CITY: 'addressCity',
  ADDRESS_COUNTRY: 'addressCountry',
  ADDRESS_COUNTRY_CODE: 'addressCountryCode',
  ADDRESS_DISPLAY_NAME: 'addressDisplayName',
  ADDRESS_STATE: 'addressState',
  ADDRESS_ZIP: 'addressZip',
  ALPHABETICALLY_SMALLEST_ENGAGEMENT_ACCOUNT_NAME:
    'alphabeticallySmallestEngagementAccountName',
  ALPHABETICALLY_SMALLEST_ENGAGEMENT_END_DATE:
    'alphabeticallySmallestEngagementEndDate',
  AVAILABILITY: 'availability',
  AVAILABILITY_NOTES: 'availabilityNotes',
  AVAILABILITY_PREFERENCES_UPDATED_AT: 'availabilityPreferencesUpdatedAt',
  AVAILABILITY_PREFERENCES_UPDATED_BY: 'availabilityPreferencesUpdatedBy',
  AVAILABILITY_PREFERENCES_UPDATED_BY_TYPE:
    'availabilityPreferencesUpdatedByType',
  AXIOM_EMPLOYEE_TYPE: 'axiomEmployeeType',
  BADGES: 'badges',
  BARRED_LOCATIONS: 'barredLocations',
  BULLHORN_ID: 'bullhornId',
  CALCULATED_DESIRED_WEEKLY_HOURS: 'calculatedDesiredWeeklyHours',
  CALCULATED_DISPLAY_NAME: 'calculatedDisplayName',
  CALCULATED_FIRST_NAME: 'calculatedFirstName',
  CALCULATED_LAST_NAME: 'calculatedLastName',
  CANDIDACY_ENGAGEMENTS: 'candidacyEngagements',
  CANDIDACY_LIST: 'candidacyList',
  COLLABORATORS: 'collaborators',
  COLLABORATOR_ID: 'collaboratorId',
  COMPENSATION: 'compensation',
  CREATED_AT: 'createdAt',
  DAYS_BEACHED: 'daysBeached',
  DAYS_DESIRED_REMOTE: 'daysDesiredRemote',
  DAYS_REQUIRED_REMOTE: 'daysRequiredRemote',
  DAYS_SINCE_STATUS_CHANGE: 'daysSinceStatusChange',
  DESIRED_COMMUTE: 'desiredCommute',
  DESIRED_ENGAGEMENT_SPECIFIC_COMPENSATION:
    'desiredEngagementSpecificCompensation',
  DISPLAY_FIRST_NAME: 'displayFirstName',
  DISPLAY_LAST_NAME: 'displayLastName',
  EMPLOYEE_TYPE: 'employeeType',
  EXPERIENCE: 'experience',
  EXPERIENCES: 'experiences',
  FIRST_NAME: 'firstName',
  FURTHEST_CANDIDACY_STATUS: 'furthestCandidacyStatus',
  FURTHEST_CANDIDACY_STATUS_UPDATED_AT: 'furthestCandidacyStatusUpdatedAt',
  PRACTICE_START_YEAR: 'practiceStartYear',
  HOME_OFFICE_ID: 'homeOfficeId',
  HOME_PHONE: 'homePhone',
  HOURLY_COMPENSATION: 'hourlyCompensation',
  HOURLY_PRICE_10HR_RESERVED: 'hourlyPrice10HrReserved',
  HOURLY_PRICE_10HR_RESERVED_LAW_FIRM: 'hourlyPrice10HrReservedLawFirm',
  HOURLY_PRICE_20HR_RESERVED: 'hourlyPrice20HrReserved',
  HOURLY_PRICE_20HR_RESERVED_LAW_FIRM: 'hourlyPrice20HrReservedLawFirm',
  HOURLY_PRICE_30HR_RESERVED: 'hourlyPrice30HrReserved',
  HOURLY_PRICE_30HR_RESERVED_LAW_FIRM: 'hourlyPrice30HrReservedLawFirm',
  HOURLY_PRICE_40HR_RESERVED: 'hourlyPrice40HrReserved',
  HOURLY_PRICE_40HR_RESERVED_LAW_FIRM: 'hourlyPrice40HrReservedLawFirm',
  HOURLY_PRICE_VARIABLE: 'hourlyPriceVariable',
  HOURLY_PRICE_VARIABLE_LAW_FIRM: 'hourlyPriceVariableLawFirm',
  ID: 'id',
  IS_OPEN_TO_ADHOC: 'isOpenToAdHoc',
  IS_OPEN_TO_COMMUTE: 'isOpenToCommute',
  IS_OPEN_TO_FULL_TIME: 'isOpenToFullTime',
  IS_OPEN_TO_PART_TIME: 'isOpenToPartTime',
  IS_OPEN_TO_REMOTE: 'isOpenToRemote',
  LANGUAGE_CEFR: 'languageCEFR',
  LANGUAGES: 'languages',
  LAST_NAME: 'lastName',
  LAST_UPDATED_BY: 'lastUpdatedBy',
  LATEST_ENGAGEMENT_ACCOUNT_NAME: 'latestEngagementAccountName',
  LATEST_ENGAGEMENT_END_DATE: 'latestEngagementEndDate',
  LAW_SCHOOL: 'lawSchool',
  MIDDLE_NAME: 'middleName',
  MOBILE_PHONE: 'mobilePhone',
  NOTICE_PERIOD: 'noticePeriod',
  OWNER_FULL_NAME: 'ownerFullName',
  OWNER_USER_ID: 'ownerUserId',
  PART_TIME_HOURS_MONDAY: 'partTimeHoursMon',
  PART_TIME_HOURS_TUESDAY: 'partTimeHoursTue',
  PART_TIME_HOURS_WEDNESDAY: 'partTimeHoursWed',
  PART_TIME_HOURS_THURSDAY: 'partTimeHoursThu',
  PART_TIME_HOURS_FRIDAY: 'partTimeHoursFri',
  PERSONAL_EMAIL: 'personalEmail',
  PREF_FULL_TIME: 'prefFullTime',
  PREF_PART_TIME_HOURS_MON: 'prefPartTimeHoursMon',
  PREF_PART_TIME_HOURS_TUE: 'prefPartTimeHoursTue',
  PREF_PART_TIME_HOURS_WED: 'prefPartTimeHoursWed',
  PREF_PART_TIME_HOURS_THU: 'prefPartTimeHoursThu',
  PREF_PART_TIME_HOURS_FRI: 'prefPartTimeHoursFri',
  PREF_AD_HOC: 'prefAdHoc',
  PREF_REMOTE: 'prefRemote',
  PREF_REMOTE_WILLING_DAYS: 'prefRemoteWillingDays',
  PREF_REMOTE_REQUIRED_DAYS: 'prefRemoteRequiredDays',
  PREF_COMMUTE: 'prefCommute',
  PREF_COMMUTE_WILLING_DAYS: 'prefCommuteWillingDays',
  PREF_COMMUTE_MAX_ONE_WAY_MINUTES: 'prefCommuteMaxOneWayMinutes',
  PREFERRED_CONTACT_METHOD: 'preferredContactMethod',
  PRICING_CURRENCY: 'pricingCurrency',
  PROFILE_STATUS: 'profileStatus',
  PROFILE_STATUS_UPDATED_AT: 'profileStatusUpdatedAt',
  PRONOUNS: 'pronouns',
  RESUME: 'resume',
  SOONEST_ENGAGEMENT_ACCOUNT_NAME: 'soonestEngagementAccountName',
  SOONEST_ENGAGEMENT_END_DATE: 'soonestEngagementEndDate',
  TAGS: 'tags',
  SKILLS: 'skills',
  UPDATED_AT: 'updatedAt',
  WEEKLY_AVAILABILITY: 'weeklyAvailability',
  WEEKLY_PRICE_FULL_TIME: 'weeklyPriceFullTime',
  WEEKLY_PRICE_FULL_TIME_LAW_FIRM: 'weeklyPriceFullTimeLawFirm',
  WORK_EMAIL: 'workEmail',
  YEARS_EXPERIENCE: 'yearsExperience',
  YEARS_OF_EXPERIENCE: 'yearsOfExperience',
} as const;
Object.freeze(Candidate);

const { CandidateStatuses, CandidateStatusLabels, MLRecommendationStatuses } =
  CandidateOpportunitiesConst;

const ProfileStatuses = {
  Active: 'Active',
  Alum: 'Alum',
  AlumDNR: 'Alum DNR',
  Beach: 'Beach',
  Certifying: 'Certifying',
  Idle: 'Idle',
  InDiligence: 'In Diligence',
  NewLead: 'New Lead',
  PendingActive: 'Pending Active',
  PendingAlum: 'Pending Alum',
  PendingAlumDNR: 'Pending Alum DNR',
  PendingBeach: 'Pending Beach',
  Rejected: 'Rejected',
  Reservoir: 'Reservoir',
  Waitlist: 'Waitlist',
} as const;

const SortedProfileStatuses = [
  ProfileStatuses.NewLead,
  ProfileStatuses.Reservoir,
  ProfileStatuses.Certifying,
  ProfileStatuses.InDiligence,
  ProfileStatuses.Waitlist,
  ProfileStatuses.PendingActive,
  ProfileStatuses.Active,
  ProfileStatuses.PendingBeach,
  ProfileStatuses.Beach,
  ProfileStatuses.Idle,
  ProfileStatuses.PendingAlum,
  ProfileStatuses.Alum,
  ProfileStatuses.Rejected,
  ProfileStatuses.AlumDNR,
  ProfileStatuses.PendingAlumDNR,
] as Array<(typeof ProfileStatuses)[keyof typeof ProfileStatuses]>;

const ProfileStatusLists = {
  allCertifiedTalent: {
    name: 'All Certified Talent',
    value: [
      ProfileStatuses.Active,
      ProfileStatuses.PendingActive,
      ProfileStatuses.Beach,
      ProfileStatuses.PendingBeach,
      ProfileStatuses.Waitlist,
      ProfileStatuses.InDiligence,
    ],
  },
  allProspects: {
    name: 'All Prospects',
    value: [
      ProfileStatuses.NewLead,
      ProfileStatuses.Reservoir,
      ProfileStatuses.Certifying,
    ],
  },
} as const;

const EmploymentStatuses = {
  Active: 'Active',
  Beach: 'Beach',
  FormerContact: 'Former Contact',
  OnLeave: 'On Leave',
  PendingBeach: 'Pending Beach',
  PendingHire: 'Pending Hire',
  PendingRetire: 'Pending Retire',
  PendingReturn: 'Pending Return',
  PreHire: 'Pre-Hire',
  Retired: 'Retired',
  RescindedHire: 'Rescinded Hire',
} as const;

export const AvailabilityStatuses = {
  AvailableNow: 'Available Now',
  AvailableSoon: 'Available Soon',
  NotAvailable: 'Not Available',
} as const;

export const AvailabilityStatusKeys = {
  AvailableNow: 'AvailableNow',
  AvailableSoon: 'AvailableSoon',
  NotAvailable: 'NotAvailable',
} as const;

export const WorkPreferences = {
  OpenToLawFirms: 'Open to Law Firm clients',
  OpenToCounsel: 'Open to Counsel opportunities',
  OpenToStructuredTeams: 'Open to Projects work',
  OpenToPermanentPlacement: 'Open to Permanent Placement',
  OpenToSecondments: 'Open to Secondments',
  OpenToMoreThanOneClient: 'Open to more than one client at a time',
  OpenToNotPerfectMatch: 'Open to opps that don’t match preferences exactly',
  OpenToOnsite: 'Open to Onsite',
  OpenToInclusionInMarketing: 'Open to inclusion in marketing',
} as const;

export const WorkPreferenceKeys = {
  OpenToLawFirms: 'OpenToLawFirms',
  OpenToCounsel: 'OpenToCounsel',
  OpenToStructuredTeams: 'OpenToStructuredTeams',
  OpenToPermanentPlacement: 'OpenToPermanentPlacement',
  OpenToSecondments: 'OpenToSecondments',
  OpenToMoreThanOneClient: 'OpenToMoreThanOneClient',
  OpenToNotPerfectMatch: 'OpenToNotPerfectMatch',
  OpenToOnsite: 'OpenToOnsite',
  OpenToInclusionInMarketing: 'OpenToInclusionInMarketing',
} as const;

export const CounselWorkPreferences = {
  OpenTo: 'Open To',
  Prefers: 'Prefers',
  None: 'None',
} as const;

const ProactiveEligibleStatuses = new Set([
  ProfileStatuses.Active,
  ProfileStatuses.Beach,
  ProfileStatuses.Waitlist,
  ProfileStatuses.PendingActive,
  ProfileStatuses.PendingBeach,
]);

const NoBioStatuses = new Set([
  ProfileStatuses.AlumDNR,
  ProfileStatuses.PendingAlumDNR,
  ProfileStatuses.Rejected,
]);

export const CandidatesConst = {
  AvailabilityStatuses,
  AvailabilityStatusKeys,
  Unavailable: 'unavailable',
  FullTime: 'fullTime',
  PartTime: 'partTime',
  ProfileStatuses,
  ProfileStatusLists,
  EmploymentStatuses,
  SortedProfileStatuses,
  ProactiveEligibleStatuses,
  NoBioStatuses,
  WorkingEmploymentStatuses: [
    EmploymentStatuses.Active,
    EmploymentStatuses.Beach,
    EmploymentStatuses.OnLeave,
    EmploymentStatuses.PendingBeach,
    EmploymentStatuses.PendingHire,
    EmploymentStatuses.PendingRetire,
    EmploymentStatuses.PendingReturn,
  ],
  ValidFulfillmentCompletionStatuses: [
    ProfileStatuses.Waitlist,
    ProfileStatuses.PendingActive,
    ProfileStatuses.Active,
    ProfileStatuses.PendingBeach,
    ProfileStatuses.Beach,
  ],
  StatusInvalidForCompletionMessage:
    'Selected Talent is not eligible for fulfillment due to their profile status.',
  // Listener api call to `GET /candidates/:id`
  Expansions: {
    engagedAccountInfo: 'engagedAccountInfo',
    baseCandidateInfo: 'baseCandidateInfo',
    candidateES: 'candidateES',
  },
  ExpansionAttrs: {
    candidateOpportunity: ['candidateStatus', 'opportunityId'],
    candidateAccount: ['isHiddenExternal', 'calculatedNotes'],
  },
  CandidateStatuses,

  // CandidateStatuses possible for furthestCandidacy in the furthest order
  // Note: needs to be an array so we can assure ordering
  FurthestCandidacyStatuses: [
    CandidateStatuses.RepBioShare,
    CandidateStatuses.ShortList,
    CandidateStatuses.Warmed,
    CandidateStatuses.WarmedYes,
    CandidateStatuses.Submitted,
    CandidateStatuses.Interviewing,
    CandidateStatuses.Selected,
  ],
  FurthestCandidacyStatusOptions: {
    [CandidateStatuses.RepBioShare]: CandidateStatusLabels.RepBioShare,
    [CandidateStatuses.ShortList]: CandidateStatusLabels.ShortList,
    [CandidateStatuses.Warmed]: CandidateStatusLabels.Warmed,
    [CandidateStatuses.WarmedYes]: CandidateStatusLabels.WarmedYes,
    [CandidateStatuses.Submitted]: CandidateStatusLabels.Submitted,
    [CandidateStatuses.Interviewing]: CandidateStatusLabels.Interviewing,
    [CandidateStatuses.Selected]: CandidateStatusLabels.Selected,
  },
  EmployeeTypes: {
    RegularEligible: 'Regular (Eligible)',
    RegularNonEligible: 'Regular (Non-Eligible)',
    FixedTermEligible: 'Fixed Term (Eligible)',
    FixedTermNonEligible: 'Fixed Term (Non-Eligible)',
    Contractor: 'Contractor',
    Temporary: 'Temporary',
  },
  availabilityPreferencesUpdatedByTypes: {
    Talent: 'TALENT',
    HQ: 'HQ',
  },
  TerminationReasons: {
    InvoluntaryNoRegret: 'Involuntary non-regrettable',
    InvoluntaryRegrettable: 'Involuntary regrettable',
    VoluntaryNoRegret: 'Voluntary non-regrettable',
    VoluntaryRegrettable: 'Voluntary regrettable',
    Death: 'Involuntary death of employee',
    Conversion: 'Conversion',
    EndContract: 'End Contract',
  },
  EngagedHoursWarningLimit: 81,
  ReservedHoursWarningLimit: 61,
  DaysUntilAvailabilityPreferencesStale: 29,
  DaysOfInactivityBeforeAutoIdle: 60,
  DaysInIdleBeforeAutoAlumReservoir: 60,
  CounselWorkPreferences,
  RateFields: [
    'displayLowHourlyRate',
    'displayHighHourlyRate',
    'displayWeeklyRate',
    'currencyType',
    'hoursPerDay',
  ],
  Badges: {
    ...AvailabilityStatuses,
    Contractor: 'Contractor',
  },
  WorkPreferences,
} as const;
Object.freeze(CandidatesConst);

const defaultBaseFields = [
  'barredLocations',
  'businessTitle',
  'calculatedDisplayName',
  'candidateHeader',
  'businessTitle',
  'hasRate',
  'id',
  'industries',
  'isAvailableToConsult',
  'isExternalView',
  'isSpotlightedLawyer',
  'searchableByClient',
  'isViewOnly',
  'lawDegreeSchools',
  'lawFirms',
  'profileImageKey',
  'profileImageName',
  'pronouns',
  'candidateSummary',
  'publicRefId',
  'restricted',
  'yearsOfExperience',
];

export const CandidateWhiteLists = {
  CLIENT_USER: {
    BASE_FIELDS_WHITELIST: [
      ...defaultBaseFields,
      'calculatedFirstName',
      'calculatedLastName',
      'addressCity',
      'addressCountry',
      'addressState',
      'badges',
      'calendar',
      'certifications',
      'degrees',
      'experiences',
      'languages',
      // special case, needs both due to SPV limits
      'weeklyAvailability',
      'weeklyAvailabilityLabel',
      'primaryPracticeArea',
      'primaryFocusArea',
    ],
    RELATED_FIELDS_WHITELIST: {
      areaOfStudy: ['id', 'name', 'category', 'createdAt', 'updatedAt'],
      barredLocations: ['id', 'name'],
      industries: ['industryValue', 'yearsOfExperience'],
      degrees: [
        'degree',
        'institution',
        'yearAwarded',
        'areaOfStudyId',
        'areaOfStudy',
      ],
      experiences: [
        'client',
        'clientDomain',
        'description',
        'startDate',
        'endDate',
        'locationName',
        'locationAddressComponents',
        'externalOpportunityName',
        'isAxiom',
        'isConfidential',
        'formattedStartDate',
        'formattedEndDate',
        'tags',
        'id',
        'name',
      ],
      languages: [
        'name',
        'languageCEFR',
        'languageProficiency',
        'languageSkill',
      ],
      certifications: ['award', 'institution', 'year'],
      lawFirms: ['client'],
      lawDegreeSchools: ['degree', 'institution', 'yearAwarded'],
    },
  },
  UNAUTHENTICATED_USER: {
    BASE_FIELDS_WHITELIST: [
      ...defaultBaseFields,
      'weeklyAvailabilityLabel',
      'businessTitle',
      'addressCountry',
      'addressState',
      'badges',
      'certifications',
      'degrees',
      'languages',
      'primaryPracticeArea',
      'primaryFocusArea',
    ],
    RELATED_FIELDS_WHITELIST: {
      barredLocations: ['id', 'name'],
      industries: ['industryValue', 'yearsOfExperience'],
      lawFirms: ['client'],
      lawDegreeSchools: ['institution'],
      certifications: ['award', 'institution', 'year'],
      degrees: ['degree', 'institution', 'yearAwarded'],
      languages: [
        'name',
        'languageCEFR',
        'languageProficiency',
        'languageSkill',
      ],
    },
  },
  NON_LP_USER: {
    BASE_FIELDS_WHITELIST: [
      ...defaultBaseFields,
      'addressCity',
      'addressCountry',
      'addressState',
      'badges',
      'businessTitle',
      'currencyType',
      'displayHighHourlyRate',
      'displayLowHourlyRate',
      'displayWeeklyRate',
      'hoursPerDay',
      'weeklyAvailability',
      'certifications',
      'degrees',
      'languages',
      'primaryPracticeArea',
      'primaryFocusArea',
    ],
    RELATED_FIELDS_WHITELIST: {
      barredLocations: ['id', 'name'],
      industries: ['industryValue', 'yearsOfExperience'],
      lawFirms: ['client'],
      lawDegreeSchools: ['institution'],
      certifications: ['award', 'institution', 'year'],
      degrees: ['degree', 'institution', 'yearAwarded'],
      languages: [
        'name',
        'languageCEFR',
        'languageProficiency',
        'languageSkill',
      ],
    },
  },
} as const;

export const PublicRefIdStartNumber = 125000;

export const CandidateStatusSortOrder = [
  CandidateStatuses.Engaged,
  CandidateStatuses.Selected,
  CandidateStatuses.Interviewing,
  CandidateStatuses.Submitted,
  CandidateStatuses.WarmedYes,
  CandidateStatuses.Warmed,
  CandidateStatuses.RepBioShare,
  MLRecommendationStatuses.AiSuggested,
  CandidateStatuses.Interested,
  CandidateStatuses.ShortList,
  CandidateStatuses.Suggested,
  CandidateStatuses.Rejected,
  CandidateStatuses.Removed,
  CandidateStatuses.Cooled,
  CandidateStatuses.TalentOptOut,
  CandidateStatuses.InterestedRejected,
  MLRecommendationStatuses.AiRemoved,
];
Object.freeze(CandidateStatusSortOrder);

export const SpotlightedTalentCountryCodes = [CountryCodesAbbreviations.US];

export const AxiomEmployeeType = {
  Insourcing: 'Insourcing',
  HQ: 'HQ',
  Outsourcing: 'Outsourcing',
  Projects: 'Projects',
} as const;

export const AxiomEmployeeTypeValues = Object.values(AxiomEmployeeType);
